import React, {useState, useEffect, useCallback} from 'react';
import {EZPRTheme} from '../../_theme';
import PropTypes from 'prop-types';
import {Button, ButtonGroup, createFilterOptions, createTheme, Paper, Popper} from '@mui/material';
import {Autocomplete, CircularProgress, TextField, ThemeProvider} from '@mui/material';
import {makeStyles} from '@mui/styles';
import {bgColorRequester, bgColorSpender, ezprOrange, useDebounce, useLocalStorage} from '../../_helpers';
import {MasterDataController} from '../../../controllers/MasterDataController';
import {moveToFirst, removeLeadingZeros} from '../../../utils/helpers';
import {checkLabelValueObject} from '../../../utils/checkObjects';
import {TooltipWrapperIssue} from '../../TooltipWrapperIssue.jsx';
import {ErrorFlow, BrickContext} from 'olympe';
import {NO_OPTIONS_LABEL} from '../../../utils/constants';

/**
 * @enum {string}
 */
MasterDataDropdown.tabs = {
    RECENTLY_USED: 'Recently used',
    ALL: 'All',
};

/**
 * @type {number}
 */
MasterDataDropdown.NUMBER_OF_RECENTLY_USED = 5;

const STYLE = {
    width: '100%',
    height: '100%',
};

/**
 * @param {MasterDataDropdown.propTypes} props
 * @return {JSX.Element}
 */
export function MasterDataDropdown(props) {
    const id = 'id-' + (Date.now() * Math.random());

    const _theme = createTheme();
    const useStyles = makeStyles(
        () => {
            return {
                root: {
                    '& .css-qzbt6i-MuiButtonBase-root-MuiIconButton-root-MuiAutocomplete-popupIndicator': {
                        borderLeft: props.error ? 'solid 1px rgba(255,0,0,1.0)' : 'solid 1px rgba(0,0,0,0.54)',
                        borderRadius: '0',
                    },
                    '& .css-113ntv0-MuiButtonBase-root-MuiIconButton-root-MuiAutocomplete-popupIndicator': {
                        borderRight: props.error ? 'solid 1px rgba(255,0,0,1.0)' : 'solid 1px rgba(0,0,0,0.54)',
                        borderRadius: '0',
                    },
                    '& .css-1glvl0p-MuiButtonBase-root-MuiIconButton-root-MuiAutocomplete-clearIndicator': {
                        marginRight: '3px',
                        borderRadius: '0',
                    },
                },
            };
        },
        {_theme},
    );
    const classes = useStyles();

    const [filters, setFilters] = useState([
        {filterName: 'company code', filterValue: props.selectedCompanyCode?.value},
        {filterName: 'plant', filterValue: props.selectedPlant?.value},
        {filterName: 'WBS', filterValue: props.selectedWBS?.value}].filter((filter) => !!filter.filterValue));

    const lsKey = `recentlyUsed:${props.property}:${filters.reduce((acc, filter) => `${filter.filterName}:${filter.filterValue}`, '')}`;

    const [valueLS, setValueLS] = useLocalStorage(lsKey, []);

    // A query is performed
    // - when the user opens the autocomplete
    // - when the user changes the text input of the autocomplete
    const [isPerformingQuery, setIsPerformingQuery] = useState(false);
    const [open, setOpen] = useState(false);
    // if filterQueryWithSearch is set to true, we want to track the value in the input field and perform queries when this text changes
    const initialValue = props.selectedValue ? props.selectedValue.value : '';
    const [textInput, setTextInput] = useState(props.filterQueryWithSearch ? initialValue : '');
    const debouncedTextInput = useDebounce(textInput, 800);
    const [queriedText, setQueriedText] = useState('');
    const [showErrorTooltip, setShowErrorTooltip] = useState(false);
    const [error, setError] = useState({code: 200, body: ''});
    // We integrated the tab information in the option state, to avoid having options array not corresponding to the current tab
    // For example, if the user switch tabs before having received the results from the API, the options and tab information
    // was not matching.
    const [options, setOptions] = useState({
        tab: props.recentlyUsed ? MasterDataDropdown.tabs.RECENTLY_USED : MasterDataDropdown.tabs.ALL,
        arr: [],
    });

    // This useEffect handles the queries to perform to get the options for the autocomplete
    // it will initiate queries when textInput changes (with debouncing)
    useEffect(() => {
        // avoid performing several queries simultaneously
        if (isPerformingQuery) {
            return;
        }

        const searchInput = debouncedTextInput?.split(' - ')[0].trim();
        // avoid performing a query of with the same input as the last query
        if (queriedText === searchInput && options.arr.length > 0) {
            return;
        }
        // avoid performing a query when the autocomplete is closed
        if (open === false) {
            return;
        }
        // start a query
        setIsPerformingQuery(true);
        setShowErrorTooltip(false);

        if (options.tab === MasterDataDropdown.tabs.ALL) {
            (async () => {
                const currentTextForQuery = searchInput;
                let data;
                try {
                    data = await MasterDataController.fetchMasterData(
                        props.getMasterData,
                        props.context,
                        props.property,
                        currentTextForQuery,
                        props.selectedCompanyCode,
                        props.selectedPlant,
                        props.selectedWBS,
                        props.materialNumber,
                        props.itemCodeSearchProp,
                        props.l3Category,
                        props.userId);
                    const formattedOptions = MasterDataController.formatMasterData(data, props.showValue, props.property);

                    setOptions((prevState) => {
                        if (prevState.tab === MasterDataDropdown.tabs.ALL) {
                            return {
                                ...prevState,
                                arr: formattedOptions,
                            };
                        } else {
                            return prevState;
                        }
                    });
                    setQueriedText(currentTextForQuery);
                } catch (err) {
                    if (err instanceof ErrorFlow) {
                        setError({code: err.getCode(), body: err.getMessage()});
                    } else {
                        setError({code: 1, body: err});
                    }
                    setShowErrorTooltip(true);
                }
                setIsPerformingQuery(false);
            })();
        } else {
            // Here we use this temp variable to force refresh the value of the local storage. This is needed when the
            // value is modified by another component (for example in the list of the account details, it could have
            // multiple WBS dropdown, and if the user change a value in the first one, the recently used dropdown of
            // the other should also be updated)
            const temp = JSON.parse(localStorage.getItem(lsKey)) ?? [];
            setOptions((prevState) => {
                return {
                    ...prevState,
                    arr: temp,
                };
            });
            setIsPerformingQuery(false);
            setValueLS(temp);
        }
    }, [debouncedTextInput, open, queriedText, options.tab, lsKey]);

    useEffect(() => {
        if (!open) {
            setOptions((prevState) => {
                return {
                    ...prevState,
                    arr: [],
                };
            });
        }
    }, [open]);

    // This useEffect maintain the filter values
    useEffect(() => {
        const tempFilters = [{filterName: 'company code', filterValue: props.selectedCompanyCode?.value},
            {filterName: 'plant', filterValue: props.selectedPlant?.value},
            {filterName: 'WBS', filterValue: props.selectedWBS?.value}].filter((filter) => !!filter.filterValue);
        setFilters(tempFilters);
        setValueLS(JSON.parse(localStorage.getItem(`recentlyUsed:${props.property}:${tempFilters.reduce((acc, filter) => `${filter.filterName}:${filter.filterValue}`, '')}`)) ?? []);
    }, [props.selectedCompanyCode,
        props.selectedPlant,
        props.selectedWBS]);

    const filterOptions = createFilterOptions({
        limit: 50,
    });

    const handleInputChange = (event) => {
        if (options.tab === MasterDataDropdown.tabs.RECENTLY_USED) {
            setOptions((prevState) => {
                return {
                    ...prevState,
                    tab: MasterDataDropdown.tabs.ALL,
                };
            });
        }

        const inputValue = event.target.value;

        // if filterQueryWithSearch is set to true, we want to track the value in the input field and perform queries when this text changes
        if (props.filterQueryWithSearch) {
            setTextInput(inputValue);
        }
    };

    const handleChange = (event, newValue) => {
        if (!newValue && props.filterQueryWithSearch) {
            setTextInput('');
        }
        if (props.valueFromSelectionOnly && typeof newValue === 'string') {
            return;
        }
        const valueValidated = checkLabelValueObject(newValue);

        // Add the fields other than label and value
        for (const key in newValue) {
            if (key !== 'label' && key !== 'value') {
                valueValidated[key] = newValue[key];
            }
        }

        // We check whether we need to add the value in the recently used (recently used enabled, current tab is ALL,
        // there's a value selected, and the value is not already in the recently used
        if (props.recentlyUsed &&
            options.tab === MasterDataDropdown.tabs.ALL &&
            !!newValue) {
            if (!valueLS.map((v) => v.value).includes(valueValidated.value)) {
                // If we need to add the value, we check if we already have the max number of recently used value, if yes,
                // we replace the oldest one with the new one.
                setValueLS(valueLS.length >= MasterDataDropdown.NUMBER_OF_RECENTLY_USED ? [...valueLS, valueValidated].slice(1, MasterDataDropdown.NUMBER_OF_RECENTLY_USED + 1) : [...valueLS, valueValidated]);
            } else {
                // If all the criteria match, but we already have the value in the recently used, we set it as the newest
                setValueLS(moveToFirst(
                    valueLS,
                    valueValidated,
                    (e1, e2) => e1.value === e2.value,
                    (arr, elem) => arr.some((e) => e.value === elem.value)));
            }
        }
        props.onChange(valueValidated, 'autocomplete');
    };

    const handleBlur = (event) => {
        if (!props.onBlur) {
            return;
        }

        const newValue = event.target.value;
        if (newValue === props.selectedValue.label) {
            return;
        }
        if (!newValue) {
            props.onBlur({label: '', value: ''}, 'autocomplete_blur');
        } else {
            props.onBlur(checkLabelValueObject(newValue), 'autocomplete_blur');
        }
    };

    const handleChangeTab = (tabName) => {
        setIsPerformingQuery(false);
        setOptions((prevState) => {
            return {
                tab: tabName,
                arr: [],
            };
        });
    };

    const getPopperInfoText = () => {
        return filters.reduce((acc, filter) => {
            return !filter.filterValue ? acc : `${acc} ${acc === '' ? 'Recently used with ' : ' and'} ${filter.filterName} ${filter.filterValue}`;
        }, '');
    };

    const recentlyUsedPopper = useCallback((params) => {
        return (
            <Popper {...params} className='custom-dropdown-menu-with-buttons'>
                <div className='pr-editor-master-data-dropdown-popper'>
                    <ButtonGroup color="primary" aria-label="outlined primary button group">
                        <Button
                            variant='text'
                            onClick={() => {
                                handleChangeTab(MasterDataDropdown.tabs.RECENTLY_USED);
                            }}
                            onMouseDown={(e) => e.preventDefault()}
                            style={{color: options.tab === MasterDataDropdown.tabs.RECENTLY_USED ? ezprOrange : '#CCC'}}
                        >
                            {MasterDataDropdown.tabs.RECENTLY_USED}
                        </Button>
                        <Button
                            variant='text'
                            onClick={() => {
                                handleChangeTab(MasterDataDropdown.tabs.ALL);
                            }}
                            onMouseDown={(e) => e.preventDefault()}
                            style={{color: options.tab === MasterDataDropdown.tabs.ALL ? ezprOrange : '#CCC'}}
                        >
                            {MasterDataDropdown.tabs.ALL}
                        </Button>
                    </ButtonGroup>
                    {
                        options.tab === MasterDataDropdown.tabs.RECENTLY_USED &&
                        <div className={'pr-editor-master-data-dropdown-popper-info'}>
                            {getPopperInfoText()}
                        </div>
                    }
                </div>
                {params.children}
            </Popper>
        );
    }, [options.tab, filters]);

    // Element
    return (
        <ThemeProvider theme={EZPRTheme}>
            <div className={classes.root}>
                <TooltipWrapperIssue
                    open={showErrorTooltip}
                    onClose={() => setShowErrorTooltip(false)}
                    error={error}
                >
                    <Autocomplete
                        data-cy={'mdDropdown_' + props.property}
                        id={id}
                        sx={{
                            backgroundColor: props.mandatoryForSpender && props.highlightMandatoryFieldForSpender ?
                                bgColorSpender :
                                props.mandatoryForRequester && props.highlightMandatoryFieldForRequester ?
                                    bgColorRequester :
                                    null,
                        }}
                        PopperComponent={props.recentlyUsed ? recentlyUsedPopper : undefined}
                        PaperComponent={(p) => <Paper {...p} style={{width: props.menuWidth}} />}
                        filterOptions={filterOptions}
                        forcePopupIcon={true}
                        clearOnBlur={false}
                        autoSelect={props.autoSelect}
                        freeSolo={props.freeSolo}
                        disabled={props.disabled}
                        options={options.arr}
                        getOptionDisabled={(option) => option.label === NO_OPTIONS_LABEL}
                        style={STYLE}
                        size={'small'}
                        open={open}
                        onOpen={() => {
                            setOpen(true);
                        }}
                        onClose={() => {
                            setOpen(false);
                        }}
                        isOptionEqualToValue={(option, value) => option.label === value.label}
                        loading={isPerformingQuery || props.loading}
                        value={props.selectedValue}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        getOptionLabel={(option) => props.property === 'Item Code' ? removeLeadingZeros(option.label) : props.showValue ? option.value : option.label}
                        renderOption={props.renderOption}
                        renderInput={(params) => (
                            <TextField
                                error={props.error}
                                {...params}
                                variant={'outlined'}
                                inputRef={props.inputRef}
                                placeholder={props.placeholder}
                                rows={1}
                                InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                        <React.Fragment>
                                            {isPerformingQuery || textInput !== debouncedTextInput || props.loading ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.startAdornment}
                                        </React.Fragment>
                                    ),
                                }}
                                onChange={handleInputChange}
                            />
                        )}
                    />
                </TooltipWrapperIssue>
            </div>
        </ThemeProvider>
    );
}

MasterDataDropdown.propTypes = {
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    selectedValue: PropTypes.object,
    filterQueryWithSearch: PropTypes.bool,
    error: PropTypes.bool,
    disabled: PropTypes.bool,
    autoSelect: PropTypes.bool,
    context: PropTypes.instanceOf(BrickContext).isRequired,
    inputRef: PropTypes.object,
    property: PropTypes.string,
    placeholder: PropTypes.string,
    getMasterData: PropTypes.object,
    selectedCompanyCode: PropTypes.object,
    selectedPlant: PropTypes.object,
    selectedWBS: PropTypes.object,
    itemCodeSearchProp: PropTypes.string,
    l3Category: PropTypes.string,
    materialNumber: PropTypes.string,
    showValue: PropTypes.bool,
    userId: PropTypes.string.isRequired,
    recentlyUsed: PropTypes.bool,
    freeSolo: PropTypes.bool,
    mandatoryForSpender: PropTypes.bool,
    mandatoryForRequester: PropTypes.bool,
    highlightMandatoryFieldForRequester: PropTypes.bool,
    highlightMandatoryFieldForSpender: PropTypes.bool,
    valueFromSelectionOnly: PropTypes.bool,
    loading: PropTypes.bool,
    renderOption: PropTypes.func,
    menuWidth: PropTypes.string,
};

MasterDataDropdown.defaultProps = {
    valueFromSelectionOnly: false,
};
